import { useState, useEffect, useMemo, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { IKnoOrganisationChild } from 'APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import {
  OrganisationProps,
  SelectOrganisations,
} from 'UIPackage/SelectOrganisationsModule/components/SelectOrganisations';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './CollaboratingOrganisationsModal.module.scss';
import { IKnmErknmFormCollaboratingOrganization } from '../interfaces/IKnmErknmFormCollaboratingOrganization';
import { getCatalogKnoLevelsSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnoLevelSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnoLevelSelectOption';
import { catalogErknmKnoLevelsGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import {
  knmGetKnoCollaboratingOrganisations,
  knmGetKnoCollaboratingOrganisationsNextPage,
} from '../actions/knmActions';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import {
  getKnmCollaboratingKnoOrganisations,
  getKnmCollaboratingOrganisationsTotalCount,
} from '../selectors/knmSelectors';

const SEARCH_INPUT_ERROR = 'Поле поиска должно содержать не менее 4 символов';

interface ICollaboratingOrganisationsModalProps {
  visible?: boolean;
  selectedOrganisations?: IKnmErknmFormCollaboratingOrganization[];
  onApply: (organisations: IKnmErknmFormCollaboratingOrganization[]) => void;
  onClose: () => void;
}

interface ICollaboratingOrganisationsModalStoreProps {
  knoOrganisations: IKnoOrganisationChild[];
  totalCount: number;
  knoLevelsSelectOptions: IKnoLevelSelectOption[];
}

const CollaboratingOrganisationsModalWithoutStore = ({
  visible,
  selectedOrganisations,
  onApply,
  onClose,
  knoOrganisations,
  totalCount,
  knoLevelsSelectOptions,
}: ICollaboratingOrganisationsModalProps & ICollaboratingOrganisationsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchInputValueApproved, setSearchInputValueApproved] = useState('');
  const [searchInputError, setSearchInputError] = useState('');
  const [knoLevel, setKnoLevel] = useState<IKnoLevelSelectOption | undefined>(undefined);
  const [organisationsParentId, setOrganisationsParentId] = useState<string | undefined>(undefined);

  const currentKnoLevelSelectOption = useMemo(() => knoLevel || knoLevelsSelectOptions[0], [
    knoLevel,
    knoLevelsSelectOptions,
  ]);

  useEffect(() => {
    if (visible) {
      setSearchInputValue('');
      setSearchInputError('');
      setKnoLevel(undefined);
      setOrganisationsParentId(undefined);
      dispatch(catalogErknmKnoLevelsGet());
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (visible && currentKnoLevelSelectOption && !knoLevel) {
      dispatch(knmGetKnoCollaboratingOrganisations('', currentKnoLevelSelectOption.code, undefined));
    }
  }, [dispatch, visible, currentKnoLevelSelectOption, knoLevel]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchInputValue(e.target.value),
    [],
  );

  const handleTextInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (searchInputValue === searchInputValueApproved) {
          return;
        }

        if (searchInputValue.length === 0 || searchInputValue.length > 3) {
          setSearchInputError('');
          setSearchInputValueApproved(searchInputValue);
          dispatch(knmGetKnoCollaboratingOrganisations(searchInputValue, knoLevel?.code, organisationsParentId));
        } else {
          setSearchInputError(SEARCH_INPUT_ERROR);
        }
      }
    },
    [dispatch, searchInputValue, knoLevel, organisationsParentId, searchInputValueApproved],
  );

  const handleTextInputBlur = useCallback(() => {
    if (searchInputValue === searchInputValueApproved) {
      return;
    }

    if (searchInputValue.length === 0 || searchInputValue.length > 3) {
      setSearchInputError('');
      setSearchInputValueApproved(searchInputValue);
      dispatch(knmGetKnoCollaboratingOrganisations(searchInputValue, knoLevel?.code, organisationsParentId));
    } else {
      setSearchInputError(SEARCH_INPUT_ERROR);
    }
  }, [dispatch, searchInputValue, knoLevel, organisationsParentId, searchInputValueApproved]);

  const handleLevelsSelectChange = useCallback(
    (value: IKnoLevelSelectOption) => {
      setKnoLevel(value);
      setOrganisationsParentId(undefined);
      dispatch(knmGetKnoCollaboratingOrganisations(searchInputValue, value?.value, undefined));
    },
    [searchInputValue, dispatch, organisationsParentId],
  );

  const organisationsList = useMemo(
    () =>
      knoOrganisations
        .filter(org => org.guid)
        .map(item => ({
          ...item,
          name: item.fullName,
          fullName: item.fullName,
          id: item.guid || '',
        })),
    [knoOrganisations],
  );

  const [selectedOrganisationsList, setSelectedOrganisationsList] = useState<
    Array<IKnmErknmFormCollaboratingOrganization>
  >([]);

  useEffect(() => {
    if (selectedOrganisations) {
      setSelectedOrganisationsList(selectedOrganisations);
    }
  }, [selectedOrganisations]);

  const [currentSelectedOrganisationsList, setCurrentSelectedOrganisationsList] = useState<Array<OrganisationProps>>(
    [],
  );

  useEffect(() => {
    setCurrentSelectedOrganisationsList(
      selectedOrganisationsList
        .filter(x => x.ervkKnoOrganizationId || x.ervkTerritoryAgencyId)
        .map(x => {
          const orgProps: OrganisationProps = {
            fullName: x.title || '',
            guid: x.guid || '',
            id: x.guid || '',
            ervkKnoOrganizationId: x.ervkKnoOrganizationId,
            ervkTerritoryAgencyId: x.ervkTerritoryAgencyId,
            ervkTerritoryAgencyVersionId: x.ervkTerritoryAgencyVersionId,
          };

          return orgProps;
        }),
    );
  }, [selectedOrganisationsList]);

  const handleSaveButtonClick = useCallback(() => {
    onApply(
      currentSelectedOrganisationsList.map(x => {
        const org: IKnmErknmFormCollaboratingOrganization = {
          organization: undefined,
          guid: x.guid,
          dateCreate: undefined,
          title: x.fullName,
          ervkKnoOrganizationId: x.ervkKnoOrganizationId,
          ervkTerritoryAgencyId: x.ervkTerritoryAgencyId,
          ervkTerritoryAgencyVersionId: x.ervkTerritoryAgencyVersionId,
          listId: uuidv4(),
        };

        return org;
      }),
    );
    onClose();
  }, [currentSelectedOrganisationsList, onApply, onClose]);

  const handleAfterClose = useCallback(() => {
    setCurrentSelectedOrganisationsList(
      selectedOrganisationsList
        .filter(x => x.organization)
        .map(x => {
          const orgProps: OrganisationProps = {
            fullName: x.title || '',
            guid: x.guid || '',
            id: x.guid || '',
            ervkKnoOrganizationId: x.ervkKnoOrganizationId,
            ervkTerritoryAgencyId: x.ervkTerritoryAgencyId,
            ervkTerritoryAgencyVersionId: x.ervkTerritoryAgencyVersionId,
          };

          return orgProps;
        }),
    );
  }, [selectedOrganisationsList]);

  const handleSelectedOrganisationsChange = useCallback(
    (organisations: OrganisationProps[]) => setCurrentSelectedOrganisationsList(organisations),
    [],
  );

  const handleOrganisationsParentIdChange = useCallback(
    (id?: string) => {
      setOrganisationsParentId(id);
      dispatch(knmGetKnoCollaboratingOrganisations(searchInputValue, knoLevel?.code, id));
    },
    [dispatch, searchInputValue, knoLevel],
  );

  const handleOrganisationsPageLoad = useCallback(() => {
    if (knoOrganisations.length < totalCount) {
      dispatch(knmGetKnoCollaboratingOrganisationsNextPage(searchInputValue, knoLevel?.code));
    }
  }, [dispatch, knoOrganisations.length, totalCount, searchInputValue, knoLevel]);

  const setKnoLevelSelectorFromSelectedParentOrganization = useCallback(
    (parentOrganizationLevel?: string) => {
      if (parentOrganizationLevel && parentOrganizationLevel !== currentKnoLevelSelectOption?.value) {
        const selectKnoLevel = knoLevelsSelectOptions?.find(
          levelOption => levelOption.value === parentOrganizationLevel,
        );

        setKnoLevel(selectKnoLevel);
      }
    },
    [currentKnoLevelSelectOption, knoLevelsSelectOptions],
  );

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title="Поиск контрольных (надзорных) организаций" />
      <ModalBody size="content" border="bottom">
        <>
          <TextInput
            className={styles.SearchInput}
            id="keyWord"
            value={searchInputValue}
            placeholder="Введите ОГРН или часть наименования КНО"
            error={searchInputError}
            onChange={handleSearchInputChange}
            onKeyDown={handleTextInputKeyDown}
            onBlur={handleTextInputBlur}
          />
          <div className={styles.Group}>
            <div className={styles.Select}>
              <div className={styles.SelectLabel}>Уровень контрольного (надзорного) органа</div>
              <SelectInput
                className={styles.SelectInput}
                id="knoLevelsSelect"
                name="knoLevelsSelect"
                size="medium"
                isClearable={false}
                options={knoLevelsSelectOptions}
                onChange={handleLevelsSelectChange}
                value={currentKnoLevelSelectOption}
              />
            </div>
          </div>
        </>
        <SelectOrganisations
          isNavigationVisible={!searchInputValue?.length}
          rootNavigation={currentKnoLevelSelectOption?.label || ''}
          organisationsTitle="Доступные организации"
          selectedOrganisationsTitle="Выбранные организации"
          organisations={organisationsList}
          isOrganisationsExpanding={!searchInputValue?.length}
          isMultiple
          selectedOrganisations={currentSelectedOrganisationsList}
          onSelected={handleSelectedOrganisationsChange}
          preExpand={setKnoLevelSelectorFromSelectedParentOrganization}
          onOrganisationsParentIdChange={handleOrganisationsParentIdChange}
          onPageLoad={handleOrganisationsPageLoad}
          isLoadButtonVisible={knoOrganisations.length < totalCount}
        />
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button
          text="Сохранить"
          color="primary"
          id="saveButton"
          size="medium"
          onClick={handleSaveButtonClick}
          disabled={!currentSelectedOrganisationsList?.length}
        />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ICollaboratingOrganisationsModalStoreProps => ({
  knoOrganisations: getKnmCollaboratingKnoOrganisations(state),
  totalCount: getKnmCollaboratingOrganisationsTotalCount(state),
  knoLevelsSelectOptions: getCatalogKnoLevelsSelectOptions(state),
});

export const CollaboratingOrganisationsModal = connect(mapStateToProps)(CollaboratingOrganisationsModalWithoutStore);
